import React, { useState } from "react";
import { usePriceManualCashSale, PriceCashSaleSchema } from "@/lib/api/risk/manualEntries";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { PriceCashSalesForm, PriceCashSalesFormData } from "./PriceCashSalesForm";
import { CashSaleStatus, RiskCashSale } from "@/lib/api/risk/cashSales/general";
import { type MarketZone } from "@/lib/api/generalContract";

interface CreateManualCashSalesFormProps {
    onSuccess: () => void;
    onCancel?: () => void;
    showCancelButton?: boolean;
    initialData?: RiskCashSale | null;
    marketZones?: MarketZone[];
}

export function CreatePriceCashSalesForm({ onSuccess, onCancel, showCancelButton = true, initialData, marketZones }: CreateManualCashSalesFormProps) {
    const { toast } = useToast();
    const createPricedCashSale = usePriceManualCashSale();
    const [isCreating, setIsCreating] = useState(false);

    const handleSubmit = async (data: PriceCashSalesFormData) => {
        if (isCreating || !initialData?.id) { return; }
        setIsCreating(true);

        try {
            // The form validation ensures data is already valid when we get here
            // Just prepare data for API - convert empty strings to undefined for optional fields
            const createDataResult = PriceCashSaleSchema.safeParse({
                base_id: initialData!.id,
                priced_quantity: data.priced_quantity,
                futures: data.futures || undefined,
                basis: data.basis || undefined,
                option_month: data?.option_month?.toString() || undefined,
                market_zone_id: data.market_zone_id || undefined,
                start_date: data.start_date || undefined,
                end_date: data.end_date || undefined,
            });
            if (!createDataResult.success) {
                toast({ title: "Invalid data" });
                return;
            }

            const result = await createPricedCashSale(initialData!.id, createDataResult.data);

            if (!result.success) {
                throw result;
            }

            toast({ title: "Manual cash sale created successfully" });
            onSuccess();
        } catch (error) {
            console.error("Failed to price manual cash sale", error);
            toast({
                title: "Failed to price manual cash sale",
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <PriceCashSalesForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={onCancel}
            showCancelButton={showCancelButton}
            isSubmitting={isCreating}
            initialData={initialData}
            marketZones={marketZones}
        />
    );
}
